import React, {useContext} from "react";
import {useNavigate} from "react-router-dom";
import {
	Box,
	Card,
	CardContent,
	CardMedia,
	Chip,
	Typography
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import {palette} from "@/constants";
import {ICourseEntity, ILessonEntity} from "@buildwitt/shapeshift-types/entities";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {useRXDStateContext} from "@/contexts/rxdProgressContext";
import {INotificationSeverity, NotificationContext} from "@buildwitt/shapeshift-identity";

interface ModuleCardProps {
	module: ILessonEntity;
	course: ICourseEntity;
	lessonNumber: number;
	completed: boolean;
	accessible: boolean;
	modules?: ILessonEntity[];
}

const formatDuration = (durationInSeconds: number): string =>
	new Date(durationInSeconds * 1000).toISOString().substr(14, 5);

export const ModuleCard = ({module, course, lessonNumber, completed, accessible, modules}: ModuleCardProps) => {
	const navigate = useNavigate();
	const mq = useMq();
	const {lessonMode, isNormalMode} = useRXDStateContext();
	const {notify} = useContext(NotificationContext);

	const handleClick = (): void => {
		if (!accessible) {
			const message = isNormalMode() 
				? "Complete previous lesson first"
				: "This lesson is not accessible in the current mode";
			notify(
				message,
				"",
				INotificationSeverity.warning,
				3000
			);
			return;
		}

		const normalizedModule = {
			...module,
			videoLength: (module as any).videoLength || module.videoDuration
		};

		navigate(`/module/${module.slug}`, {
			state: {course, module: normalizedModule, lessonNumber, modules}
		});
	};

	return (
		<Card
			onClick={handleClick}
			sx={{
				backgroundColor: palette.grey90,
				borderRadius: "20px",
				overflow: "hidden",
				border: `1px solid ${completed ? palette.orange : palette.grey15}`,
				cursor: accessible ? "pointer" : "not-allowed",
				opacity: accessible ? 1 : 0.6,
				position: "relative",
				"&:hover": {
					borderColor: accessible ? palette.orange : palette.grey15
				}
			}}
		>
			{!mq.mobile && (
				<Box sx={{margin: "16px", marginBottom: 0}}>
					<CardMedia
						component="img"
						height="229px"
						image={module.videoThumbnailUrl}
						alt={module.title}
						sx={{transition: "0.3s", borderRadius: "16px"}}
					/>
				</Box>
			)}

			<CardContent>
				<Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px"}}>
					<Chip
						label={formatDuration(module.videoDuration)}
						size="small"
						sx={{
							color: palette.white,
							border: `1px solid ${palette.greyBorder}`
						}}
					/>
					{completed && (
						<CheckCircleIcon sx={{color: palette.orange, fontSize: "24px"}} />
					)}
					{!accessible && (
						<LockIcon sx={{color: palette.greyBorder, fontSize: "24px"}} />
					)}
				</Box>
				<Typography variant="h6" color={palette.white}>
					{module.title}
				</Typography>
			</CardContent>
		</Card>
	);
};
